import { cn } from '@@/lib/utils';
import { fetchUserAttributes } from 'aws-amplify/auth';
import { signOut } from 'aws-amplify/auth';
import React, { lazy, Suspense, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import '@@/ui/theme/shadcn.css';
import { useActiveBanner } from '@@/hooks/useActiveBanner';
import { BannerAlert } from '@@/ui/components/BannerAlert/BannerAlert';
import { useGlobal } from '@@/contexts/GlobalProvider';

const TopBar = lazy(() => import('./TopBar'));

const SideBar = lazy(() => import('./SideBar'));

const CxPortalWrapper = () => {
  const { setColorScheme } = useGlobal();
  const [isSideNavExpanded, setIsSideNavExpanded] = useState(false);
  const [userEmail, setUserEmail] = useState<string>('');

  useEffect(() => {
    (async () => {
      const result = await fetchUserAttributes();

      if (!result || Object.keys(result).length === 0) {
        signOut();
        return;
      }

      const userEmail = result.email;
      setUserEmail(userEmail || '');
    })();
  }, []);
  useEffect(() => {
    setColorScheme('cxportal');
  }, []);

  const location = useLocation();
  const pageSlug = getPortalPageSlug(location.pathname);

  return (
    <>
      <TopBar userEmail={userEmail} isExpanded={isSideNavExpanded} />
      <SideBar isExpanded={isSideNavExpanded} setIsExpanded={setIsSideNavExpanded} />
      {/* Global portal banner just below top bar */}
      <PortalBannerRail isExpanded={isSideNavExpanded} pageSlug={pageSlug} />
      <Suspense>
        <main
          className={cn(
            'transition-all duration-300 h-[calc(100vh-3rem)] overflow-auto absolute scrollbar-custom scrollbar-gutter-stable',
            isSideNavExpanded ? 'w-[calc(100%-250px)] ml-[250px]' : 'w-[calc(100%-43px)] ml-[43px]',
            useBannerTopOffset(pageSlug)
          )}
        >
          <Outlet />
        </main>
      </Suspense>
    </>
  );
};

export default CxPortalWrapper;

function PortalBannerRail({ isExpanded, pageSlug }: { isExpanded: boolean; pageSlug?: string }) {
  const { banner, isLoading } = useActiveBanner(pageSlug);
  if (isLoading || !banner) return null;
  return (
    <div className={cn('fixed top-12 left-0 right-0 z-40', isExpanded ? 'left-[250px]' : 'left-[43px]')}>
      <BannerAlert brand='portal' message={banner.message} severity={banner.severity} updatedAt={banner.updatedAt} />
    </div>
  );
}

function useBannerTopOffset(pageSlug?: string) {
  const { banner, isLoading } = useActiveBanner(pageSlug);
  return !isLoading && banner ? 'top-[88px]' : 'top-12';
}

function getPortalPageSlug(pathname: string): string | undefined {
  // Map routes to backend page slugs
  if (pathname.startsWith('/portal/qic/agents')) return 'agent-management';
  // Add additional mappings as needed
  return undefined;
}
